import { Clock3 } from "lucide-react"
import { useEffect, useState } from "react"
import {
  SEARCH_RANKING_RUN_STATUS,
  type SearchRankingOverview,
  type SearchRankingRunStatus,
} from "@nova-ai/shared"

import { formatDateTimeInTimeZone, formatRelativeTime } from "../../lib/format-date"
import { humanizeEnumLabel } from "../../lib/enum-label"
import { StatusBadge, type BadgeTone } from "../ui/status-badge"
import { Tooltip } from "../ui/tooltip"

type SearchRankingLatestRun = SearchRankingOverview["latestRun"]

const STATUS_TONES: Record<SearchRankingRunStatus, BadgeTone> = {
  [SEARCH_RANKING_RUN_STATUS.valid]: "success",
  [SEARCH_RANKING_RUN_STATUS.partial]: "warning",
  [SEARCH_RANKING_RUN_STATUS.blocked]: "destructive",
  [SEARCH_RANKING_RUN_STATUS.invalid]: "destructive",
}

export function SearchRankingFreshness({
  latestRun,
  timeZone,
}: {
  latestRun: SearchRankingLatestRun
  timeZone: string
}) {
  if (!latestRun) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <Clock3 className="size-3.5" aria-hidden="true" />
        No scrapes yet
      </span>
    )
  }

  return <ScrapeTimeDisclosure latestRun={latestRun} timeZone={timeZone} />
}

function ScrapeTimeDisclosure({
  latestRun,
  timeZone,
}: {
  latestRun: NonNullable<SearchRankingLatestRun>
  timeZone: string
}) {
  const relativeTime = useRelativeTime(latestRun.completedAt)
  const exactTime = formatDateTimeInTimeZone(latestRun.completedAt, timeZone)
  const statusLabel = humanizeEnumLabel(latestRun.status)
  const shortLabel = `Last scrape ${relativeTime}`
  const accessibleLabel = `${shortLabel}. ${exactTime} ${timeZone}. Status ${statusLabel}`

  return (
    <Tooltip
      content={
        <div className="space-y-1.5">
          <p className="font-medium tabular-nums">{exactTime}</p>

          <p className="text-popover-foreground/70">{timeZone}</p>

          <StatusBadge tone={STATUS_TONES[latestRun.status]} size="sm">
            {statusLabel}
          </StatusBadge>
        </div>
      }
    >
      <button
        type="button"
        aria-label={accessibleLabel}
        className="inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-card px-2.5 py-1 text-xs text-muted-foreground shadow-card transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <Clock3 className="size-3.5" aria-hidden="true" />

        {shortLabel}
      </button>
    </Tooltip>
  )
}

function useRelativeTime(completedAt: string): string {
  const [label, setLabel] = useState(() => formatRelativeTime(completedAt))

  useEffect(() => {
    setLabel(formatRelativeTime(completedAt))
    const intervalId = window.setInterval(() => {
      setLabel(formatRelativeTime(completedAt))
    }, 30_000)
    return () => window.clearInterval(intervalId)
  }, [completedAt])

  return label
}
